/**
 * Unified Toast Service for VideoDB Application
 * Provides consistent toast notifications across the entire app
 */

import { ref } from 'vue'
import { captureError } from './errorReporting.js'

// Toast types and their configurations
const TOAST_TYPES = {
  success: {
    bgColor: 'bg-green-500',
    textColor: 'text-white',
    icon: 'checkmark',
    defaultDuration: 3000
  },
  error: {
    bgColor: 'bg-red-500',
    textColor: 'text-white',
    icon: 'error',
    // Error toasts include a notes modal and remain until explicitly dismissed.
    defaultDuration: 0
  },
  warning: {
    bgColor: 'bg-yellow-500',
    textColor: 'text-white',
    icon: 'warning',
    defaultDuration: 4000
  },
  info: {
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
    icon: 'info',
    defaultDuration: 3000
  }
}

// Global toast state
const toasts = ref([])
let toastIdCounter = 0

class ToastService {
  constructor() {
    this.toasts = toasts
  }

  /**
   * Add a new toast to the queue
   * @param {string} type - Toast type (success, error, warning, info)
   * @param {string} message - Toast message
   * @param {Object} options - Toast options
   * @returns {string} - Toast ID
   */
  addToast(type, message, options = {}) {
    const config = TOAST_TYPES[type]
    if (!config) {
      console.warn(`Unknown toast type: ${type}`)
      return null
    }

    const toastId = `toast-${++toastIdCounter}`
    const duration = options.duration || config.defaultDuration
    const dismissible = options.dismissible !== false // Default to true

    const toast = {
      id: toastId,
      type,
      message,
      bgColor: config.bgColor,
      textColor: config.textColor,
      icon: config.icon,
      duration,
      dismissible,
      reportContext: options.reportContext || null,
      timestamp: Date.now()
    }

    this.toasts.value.push(toast)

    // Auto-dismiss if duration is set
    if (duration > 0) {
      setTimeout(() => {
        this.removeToast(toastId)
      }, duration)
    }

    return toastId
  }

  /**
   * Remove a toast by ID
   * @param {string} toastId - Toast ID to remove
   */
  removeToast(toastId) {
    const index = this.toasts.value.findIndex(toast => toast.id === toastId)
    if (index > -1) {
      this.toasts.value.splice(index, 1)
    }
  }

  /**
   * Clear all toasts
   */
  clearAll() {
    this.toasts.value = []
  }

  /**
   * Show success toast
   * @param {string} message - Success message
   * @param {Object} options - Toast options
   * @returns {string} - Toast ID
   */
  success(message, options = {}) {
    return this.addToast('success', message, options)
  }

  /**
   * Show error toast
   * @param {string} message - Error message
   * @param {Object} options - Toast options
   * @returns {string} - Toast ID
   */
  error(message, options = {}) {
    captureError(message, { source: 'toast' })
    return this.addToast('error', message, options)
  }

  /**
   * Show warning toast
   * @param {string} message - Warning message
   * @param {Object} options - Toast options
   * @returns {string} - Toast ID
   */
  warning(message, options = {}) {
    return this.addToast('warning', message, options)
  }

  /**
   * Show info toast
   * @param {string} message - Info message
   * @param {Object} options - Toast options
   * @returns {string} - Toast ID
   */
  info(message, options = {}) {
    return this.addToast('info', message, options)
  }
}

// Create singleton instance
const toastService = new ToastService()

export default toastService
export { TOAST_TYPES }
